import {
    Checkbox,
    Box,
    useTheme,
    Typography,
    Button,
    Paper,
    Tooltip,
    Chip,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import { tokens } from "../../theme";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { djangoApi } from "../../utils/axios/axios.utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import handleDownloadData from "./excel-handler";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";

const TableAcertosFuturos = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

    const { data, setAllKits } = props;

    const [totalValue, setTotalValue] = useState(0);
    const [totalToPayPitaya, setTotalToPayPitaya] = useState(0);
    const [isLoadingCheck, setIsLoadingCheck] = useState(false);
    const [isLoadingFaturaCheck, setIsLoadingFaturaCheck] = useState(false);
    const [loadingFaturaId, setLoadingFaturaId] = useState(null);

    const removeEmailList = [
        "vanessabbrun@gmail.com",
        "vaabrun@gmail.com",
        "financeiro@pitayajoias.com.br",
    ];

    const getPaidValue = (kit) => {
        return Number(kit?.total_paid_to_pitaya ?? 0);
    };

    const getAmountDueToCompany = (kit) => {
        return Number(kit?.total_to_pay_pitaya ?? 0);
    };

    const getRemainingValue = (kit) => {
        return Number(kit?.remaining_to_pay_pitaya ?? 0);
    };

    const getOverpaidValue = (kit) => {
        return Number(kit?.overpaid_to_seller ?? 0);
    };

    const getPaymentStatus = (kit) => {
        const backendStatus = kit?.payment_status || "pending";

        const labels = {
            paid: "Pago",
            in_progress: "Em andamento",
            pending: "Ainda não",
        };

        return {
            status: backendStatus,
            label: labels[backendStatus] || "Ainda não",
            paidValue: getPaidValue(kit),
            amountDueToCompany: getAmountDueToCompany(kit),
            remainingValue: getRemainingValue(kit),
            overpaidValue: getOverpaidValue(kit),
        };
    };

    const getFilteredData = () => {
        return (data || [])
            .filter(
                (row) =>
                    !row?.seller?.nome?.includes("TONIDANDEL") &&
                    !removeEmailList.includes(row?.seller?.email)
            )
            .sort((a, b) => {
                const dateA = a?.pay_date || "";
                const dateB = b?.pay_date || "";

                const dateComparison = dateA.localeCompare(dateB);

                if (dateComparison !== 0) {
                    return dateComparison;
                }

                const nameA = a?.seller?.nome || "";
                const nameB = b?.seller?.nome || "";

                return nameA.localeCompare(nameB, "pt-BR", {
                    sensitivity: "base",
                });
            });
    };

    useEffect(() => {
        const filtered = getFilteredData();

        if (filtered.length > 0) {
            const totalKitValue = filtered.reduce(
                (acc, curr) => acc + Number(curr.total_value || 0),
                0
            );

            const totalPitayaValue = filtered.reduce(
                (acc, curr) => acc + getAmountDueToCompany(curr),
                0
            );

            setTotalValue(totalKitValue);
            setTotalToPayPitaya(totalPitayaValue);
        } else {
            setTotalValue(0);
            setTotalToPayPitaya(0);
        }
    }, [data]);

    function formatDateToBrazilian(dateString) {
        if (!dateString) return " - ";
        const [year, month, day] = dateString.split("-");
        return `${day}/${month}/${year}`;
    }

    const formatNumber = (val) => {
        if (!val && val !== 0) return " - ";

        return Number(val).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    function formatDateBR(isoString) {
        if (!isoString) return " - ";

        try {
            const date = new Date(isoString);

            if (isNaN(date.getTime())) return " - ";

            return date.toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return " - ";
        }
    }

    const updateCheckKits = async (kitData) => {
        setIsLoadingCheck(true);

        try {
            const res = await djangoApi.post(
                "products/kits_active/update_ischecked_kit/",
                kitData,
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            setIsLoadingCheck(false);

            if (res.status === 201) {
                setAllKits((prev) =>
                    prev.map((kit) =>
                        kit.id === kitData.id
                            ? { ...kit, is_checked: !kit.is_checked }
                            : kit
                    )
                );
            }
        } catch (err) {
            console.log("Erro ao alterar as aplicações", err);
            setIsLoadingCheck(false);
        }
    };

    const handleCheckIsChecked = async (row) => {
        if (isLoadingCheck) return;
        await updateCheckKits(row);
    };

    const updateFaturaKits = async (kitData) => {
        setIsLoadingFaturaCheck(true);
        setLoadingFaturaId(kitData.id);

        const now = new Date().toISOString();

        const payload = {
            id: kitData.id,
            generated_bill: !kitData.generated_bill,
        };

        try {
            const res = await djangoApi.post(
                "products/kits_active/update_fatura_ischecked_kit/",
                payload,
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            setIsLoadingFaturaCheck(false);
            setLoadingFaturaId(null);

            if (res.status === 202 || res.status === 200) {
                setAllKits((prev) =>
                    prev.map((k) =>
                        k.id === kitData.id
                            ? {
                                ...k,
                                generated_bill: payload.generated_bill,
                                generated_last_date_bill: now,
                                generated_bill_counter:
                                    res?.data?.generated_bill_counter,
                            }
                            : k
                    )
                );
            }
        } catch (err) {
            console.log("Erro ao alterar as aplicações", err);
            setIsLoadingFaturaCheck(false);
            setLoadingFaturaId(null);
        }
    };

    const NAME_COLUMN_PADDING_LEFT = 1.5;

    const headerGridCols = isMdDown
        ? "24px 1.4fr 1fr 1fr 0.7fr 0.8fr 1fr"
        : "24px 1.5fr 0.9fr 0.9fr 0.6fr 0.9fr 1fr 1.2fr 0.9fr 0.9fr";

    const rowGridCols = headerGridCols;

    const filteredData = getFilteredData();

    const totalVerified = filteredData.filter((row) => Boolean(row.is_checked)).length;
    const totalNotVerified = filteredData.length - totalVerified;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                p: { xs: 1.5, md: 2.5 },
            }}
        >
            {/* Header resumo */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", md: "flex-end" },
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                    marginBottom: 1,
                }}
            >
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                        size="small"
                        label={`Total Kits R$ ${formatNumber(totalValue)}`}
                        color="success"
                        sx={{ fontWeight: 700 }}
                    />

                    <Chip
                        size="small"
                        label={`A pagar Pitaya R$ ${formatNumber(totalToPayPitaya)}`}
                        color="info"
                        sx={{ fontWeight: 700 }}
                    />
                    <Chip
                        size="small"
                        label={`Verificados: ${totalVerified}`}
                        color="secondary"
                        sx={{ fontWeight: 700, marginLeft: 10 }}
                    />
                    <Chip
                        size="small"
                        label={`Não verificados: ${totalNotVerified}`}
                        color="warning"
                        sx={{ fontWeight: 700 }}
                    />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDownloadData(filteredData || [])}
                        startIcon={
                            <FontAwesomeIcon
                                icon={faFileExcel}
                                color={colors.greenAccent[500]}
                            />
                        }
                        sx={{
                            textTransform: "none",
                            borderColor: colors.greenAccent[500],
                            color: colors.greenAccent[500],
                            "&:hover": {
                                borderColor: colors.greenAccent[400],
                            },
                        }}
                    >
                        Exportar
                    </Button>
                </Box>
            </Box>

            {/* Header da tabela */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: rowGridCols,
                    gap: { xs: 1.5, md: 2 },
                    position: "sticky",
                    top: 50,
                    zIndex: 5,
                    py: 1,
                    px: 1,
                    borderBottom: `1px solid ${colors.grey[600]}`,
                    backgroundColor:
                        theme.palette.mode === "dark" ? colors.pink[800] : "#fff",
                    backdropFilter: "blur(6px)",
                }}
            >
                <Typography variant="caption" color="whitesmoke" />

                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="left"
                    fontWeight="bold"
                    sx={{
                        pl: NAME_COLUMN_PADDING_LEFT,
                    }}
                >
                    Nome
                </Typography>

                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="center"
                    fontWeight="bold"
                >
                    Vencimento
                </Typography>

                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="center"
                    fontWeight="bold"
                >
                    KIT
                </Typography>
                
                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="center"
                    fontWeight="bold"
                >
                    Case
                </Typography>

                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="center"
                    fontWeight="bold"
                >
                    Verificado?
                </Typography>

                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="center"
                    fontWeight="bold"
                >
                    Fatura
                </Typography>

                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="center"
                    fontWeight="bold"
                >
                    Pagamento
                </Typography>

                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="center"
                    fontWeight="bold"
                >
                    Data Fatura
                </Typography>

                <Typography
                    variant="caption"
                    color="whitesmoke"
                    textAlign="center"
                    fontWeight="bold"
                >
                    Valor
                </Typography>
            </Box>

            {/* Linhas */}
            {filteredData &&
                filteredData.length > 0 &&
                filteredData.map((row, i) => {
                    const isRowLoading = loadingFaturaId === row.id;
                    const paymentStatus = getPaymentStatus(row);

                    const isPaid = paymentStatus.status === "paid";
                    const isInProgress = paymentStatus.status === "in_progress";

                    const paymentColor = isPaid
                        ? "#7CFC00"
                        : isInProgress
                            ? colors.blueAccent[300]
                            : colors.redAccent[300];
                

                    return (
                        <Paper
                            component={Box}
                            key={row.id ?? i}
                            variant="outlined"
                            sx={{
                                display: "grid",
                                gridTemplateColumns: rowGridCols,
                                gap: { xs: 1.5, md: 2 },
                                py: 1,
                                px: 1,
                                borderRadius: 1.5,
                                backgroundColor: colors.pink[800],
                                color: colors.grey[800],
                                transition: "transform .08s ease",
                                "&:hover": {
                                    backgroundColor: colors.pink[700],
                                    transform: "translateY(-1px)",
                                },
                                "&:nth-of-type(even)": {
                                    backgroundColor:
                                        theme.palette.mode === "dark"
                                            ? colors.pink[700]
                                            : colors.pink[100],
                                },
                            }}
                        >
                            {/* # */}
                            <Typography
                                variant="caption"
                                sx={{
                                    alignSelf: "center",
                                    color: colors.grey[300],
                                }}
                            >
                                {i + 1}
                            </Typography>

                            {/* Nome */}
                            <Button
                                component={Link}
                                to={`/vendedoras/${row.seller.slug}`}
                                state={{ sellerData: row.seller }}
                                variant="text"
                                disableRipple
                                sx={{
                                    justifySelf: "start",
                                    justifyContent: "flex-start",
                                    minWidth: 0,
                                    maxWidth: "100%",

                                    py: 0,
                                    pr: 0,
                                    pl: NAME_COLUMN_PADDING_LEFT,

                                    textTransform: "none",
                                    fontSize: "13px",
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    color: colors.grey[100],
                                    textAlign: "left",
                                    transition: "color 0.15s ease",

                                    "&:hover": {
                                        backgroundColor: "transparent",
                                        color: colors.pink[400],
                                        textDecoration: "underline",
                                        textUnderlineOffset: "3px",
                                    },
                                }}
                            >
                                {row.seller.nome}
                            </Button>
                            {/* Vencimento */}
                            <Typography
                                variant="body2"
                                textAlign="center"
                                sx={{
                                    alignSelf: "center",
                                    color: colors.grey[200],
                                }}
                            >
                                {formatDateToBrazilian(row.pay_date)}
                            </Typography>

                            {/* Kit */}
                            <Typography
                                variant="body2"
                                textAlign="center"
                                sx={{
                                    alignSelf: "center",
                                    color: colors.grey[200],
                                }}
                            >
                                {row.kit_number}
                            </Typography>
                            
                            {/* Case Model */}
                            <Typography
                                variant="body2"
                                textAlign="center"
                                sx={{
                                    alignSelf: "center",
                                    color: colors.grey[200],
                                }}
                            >
                                {row.case_model}
                            </Typography>

                            {/* Verificado */}
                            <Box sx={{ alignSelf: "center", textAlign: "center" }}>
                                <Checkbox
                                    size="small"
                                    checked={Boolean(row.is_checked)}
                                    onChange={() => handleCheckIsChecked(row)}
                                    disabled={isLoadingCheck}
                                    sx={{
                                        p: 0.5,
                                        color: colors.grey[300],
                                        "&.Mui-checked": {
                                            color: colors.blueAccent[400],
                                        },
                                    }}
                                />
                            </Box>

                            {/* Fatura Gerada */}
                            <Tooltip
                                title={
                                    isRowLoading
                                        ? ""
                                        : row.generated_bill
                                            ? "Clique para marcar como pendente"
                                            : "Clique para marcar como gerado"
                                }
                                disableHoverListener={isRowLoading}
                                arrow
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        color={row.generated_bill ? "success" : "default"}
                                        variant={row.generated_bill ? "filled" : "outlined"}
                                        label={
                                            isRowLoading
                                                ? "Atualizando..."
                                                : row.generated_bill
                                                    ? "Gerado"
                                                    : "Pendente"
                                        }
                                        icon={
                                            isRowLoading ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : row.generated_bill ? (
                                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                                            ) : (
                                                <CancelIcon sx={{ fontSize: 16 }} />
                                            )
                                        }
                                        sx={{
                                            fontWeight: 700,
                                            cursor: isRowLoading ? "default" : "pointer",
                                        }}
                                        onClick={() => {
                                            if (!isRowLoading && !isLoadingFaturaCheck) {
                                                updateFaturaKits(row);
                                            }
                                        }}
                                        disabled={isRowLoading}
                                        aria-busy={isRowLoading}
                                    />
                                </Box>
                            </Tooltip>

                            {/* Pagamento */}
                            <Tooltip
                                title={
                                    isPaid
                                        ? `Devido: R$ ${formatNumber(
                                            paymentStatus.amountDueToCompany
                                        )} | Pago: R$ ${formatNumber(
                                            paymentStatus.paidValue
                                        )} | Saldo: R$ ${formatNumber(
                                            paymentStatus.remainingValue
                                        )}`
                                        : isInProgress
                                            ? `Devido: R$ ${formatNumber(
                                                paymentStatus.amountDueToCompany
                                            )} | Pago: R$ ${formatNumber(
                                                paymentStatus.paidValue
                                            )} | Falta: R$ ${formatNumber(
                                                paymentStatus.remainingValue
                                            )}`
                                            : `Devido: R$ ${formatNumber(
                                                paymentStatus.amountDueToCompany
                                            )} | Nenhum pagamento registrado`
                                }
                                arrow
                            >
                                <Box
                                    sx={{
                                        alignSelf: "center",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 0.5,
                                        color: paymentColor,
                                    }}
                                >

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            textAlign="center"
                                            sx={{
                                                color: paymentColor,
                                                fontWeight: 700,
                                                lineHeight: 1.1,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {paymentStatus.label}
                                        </Typography>

                                        {(isPaid || isInProgress) && (
                                            <Typography
                                                variant="caption"
                                                textAlign="center"
                                                sx={{
                                                    color: paymentColor,
                                                    fontWeight: 700,
                                                    lineHeight: 1.1,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                Pago: R$ {formatNumber(paymentStatus.paidValue)}
                                            </Typography>
                                        )}

                                        <Typography
                                            variant="caption"
                                            textAlign="center"
                                            sx={{
                                                color: colors.grey[200],
                                                fontWeight: 600,
                                                lineHeight: 1.1,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            Devido: R${" "}
                                            {formatNumber(paymentStatus.amountDueToCompany)}
                                        </Typography>
                                        {paymentStatus.overpaidValue > 0 && (
                                            <Typography
                                                variant="caption"
                                                textAlign="center"
                                                sx={{
                                                    color: "#FFD166",
                                                    fontWeight: 700,
                                                    lineHeight: 1.1,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                A devolver: R$ {formatNumber(paymentStatus.overpaidValue)}
                                            </Typography>
                                        )}

                                        {isInProgress && (
                                            <Typography
                                                variant="caption"
                                                textAlign="center"
                                                sx={{
                                                    color: colors.grey[200],
                                                    fontWeight: 600,
                                                    lineHeight: 1.1,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                Falta: R${" "}
                                                {formatNumber(paymentStatus.remainingValue)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Tooltip>

                            {/* Data Fatura */}
                            <Tooltip
                                title={
                                    row.generated_bill
                                        ? `Relatório gerado ${row.generated_bill_counter} vez${row.generated_bill_counter === 1 ? "" : "es"
                                        }`
                                        : ""
                                }
                                disableHoverListener={!row.generated_bill}
                                arrow
                                placement="top"
                            >
                                <Typography
                                    variant="body2"
                                    textAlign="center"
                                    sx={{
                                        alignSelf: "center",
                                        fontWeight: 700,
                                        color: colors.grey[100],
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {row.generated_bill
                                        ? formatDateBR(row.generated_last_date_bill)
                                        : " - "}{" "}
                                    {row.generated_bill_counter > 0
                                        ? `( ${row.generated_bill_counter} )`
                                        : null}
                                </Typography>
                            </Tooltip>

                            {/* Valor bruto do kit */}
                            <Typography
                                variant="body2"
                                textAlign="center"
                                sx={{
                                    alignSelf: "center",
                                    fontWeight: 800,
                                    color: "#27ae60",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                R$ {formatNumber(row.total_value)}
                            </Typography>
                        </Paper>
                    );
                })}
        </Box>
    );
};

export default TableAcertosFuturos;